import { useCallback, useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { listQueued, flushQueue } from '../lib/offlineQueue.js';

/**
 * Mounted once in AppShell.jsx — surfaces how many drafts are waiting to
 * upload, and syncs them automatically the moment the browser's `online`
 * event fires (plus once on mount, for the case the app was already open
 * when the connection came back).
 */
export function useOfflineQueue() {
  const queryClient = useQueryClient();
  const [queuedCount, setQueuedCount] = useState(0);
  const [isSyncing, setIsSyncing] = useState(false);

  const refreshCount = useCallback(async () => {
    setQueuedCount((await listQueued()).length);
  }, []);

  const syncNow = useCallback(async () => {
    setIsSyncing(true);
    try {
      const synced = await flushQueue();
      await refreshCount();
      if (synced > 0) {
        queryClient.invalidateQueries({ queryKey: ['invoices'] });
        queryClient.invalidateQueries({ queryKey: ['gold-purchases'] });
        queryClient.invalidateQueries({ queryKey: ['repairs'] });
      }
    } finally {
      setIsSyncing(false);
    }
  }, [queryClient, refreshCount]);

  useEffect(() => {
    refreshCount();
  }, [refreshCount]);

  useEffect(() => {
    window.addEventListener('online', syncNow);
    return () => window.removeEventListener('online', syncNow);
  }, [syncNow]);

  return { queuedCount, isSyncing, syncNow };
}

export default useOfflineQueue;
